import { CancelToken } from 'axios';
import { Component } from 'react';
import { Field } from 'react-final-form';
import { WithTranslation, withTranslation } from 'react-i18next';
import { connect } from 'react-redux';

import { CompanyI } from '~src/interfaces/company.interface';
import { PackName } from '~src/interfaces/cycle.interface';
import { AnswerValue, SelectionValue } from '~src/interfaces/question.interface';
import { Segment, SegmentType } from '~src/interfaces/segment.interface';
import { StoreI } from '~src/rootReducer';
import { SelectionType } from '~src/services/api/pulseCheck';
import { fetchSearchSegments } from '~src/services/fetch/fetchSearchSegments.service';
import { validatorRequired } from '~src/services/validators';
import LoadingSpinner from '~src/shared/loadingSpinner/LoadingSpinner';
import { FieldType } from '~src/types';
import PeopleChips from '~src/modules/surveys/components/schedules/scheduleItem/templates/sections/customInputs/PeopleChips';

import Card from './Card';
import { SendButton } from './card.sc';
import { uniqBy } from 'lodash';

interface DefaultProps {
  index: number;
  saveLoading: boolean;
}

interface OwnProps extends DefaultProps {
  selectionType: PackName;
  currentCard: SelectionType | null;
  onSubmit: (value: SelectionValue) => void;
}

interface StateProps {
  company: CompanyI | null;
  initialValues: {
    selectedPeople: Segment[];
  };
  noneRemovableIds: string[];
}

type Props = OwnProps & StateProps & WithTranslation;

const getLockedSegments = (type: PackName, current: SelectionType) => {
  if (type === PackName.CustomSelector) {
    return [...(current.managerSelection || []), ...(current.selfSelection || [])];
  } else if (type === PackName.ManagersSelection) {
    return [...(current.customSelection || []), ...(current.selfSelection || [])];
  } else if (type === PackName.SelfSelection) {
    return [...(current.customSelection || []), ...(current.managerSelection || [])];
  }

  return [];
};

class SelectionCard extends Component<Props> {
  static defaultProps: DefaultProps = {
    index: 0,
    saveLoading: false,
  };

  renderSendButton = (__: (field: string, value: any) => void, saveLoading: boolean) => {
    const card = this.props.currentCard;
    let buttonTypeKey = '.send';
    if (card && card.asked && card.asked.length > 0) {
      buttonTypeKey = '.update';
    }
    if (!saveLoading) {
      return (
        <SendButton type="submit" data-translation={`x.questions.selection.button${buttonTypeKey}`}>
          {this.props.t(`x.questions.selection.button${buttonTypeKey}`)}
        </SendButton>
      );
    }
    return (
      <div>
        <LoadingSpinner color="white" />
      </div>
    );
  };

  typedSearch = (value: string, cancelToken: CancelToken) =>
    fetchSearchSegments(value, cancelToken, [SegmentType.User]);

  renderChips = ({ input, meta: { touched, error } }: FieldType) => {
    const onChangePeople = (param: any) => input.onChange(param); // TODO NEED TO WAIT FOR CHIPS REFRACTORING
    return (
      <PeopleChips
        fetchSuggestions={this.typedSearch}
        chipsName="about"
        noneRemovableIds={this.props.noneRemovableIds}
        selectedPeople={input.value ? input.value : []}
        onChangePeople={onChangePeople}
        errorTxt={touched ? error : null}
        t={this.props.t}
        placeHolderKey="x.questions.selection.chips.placeholder"
      />
    );
  };

  renderField = () => (
    <Field name="selectedPeople" component={this.renderChips} validate={validatorRequired} />
  );

  submitting = (values: AnswerValue | SelectionValue) => {
    const filteredPeople = (values as SelectionValue).selectedPeople.filter(
      (item) => !this.props.noneRemovableIds.includes(item.id),
    );
    this.props.onSubmit({ selectedPeople: filteredPeople });
  };

  render() {
    const { t } = this.props;
    const cardTitle = `x.questions.card.selection_card.${this.props.selectionType}`;
    if (this.props.currentCard) {
      return (
        <Card
          subject={this.props.currentCard.about}
          key={`questionForm_selection${this.props.currentCard?.about?.id}`}
          onSubmit={this.submitting}
          actionPanel={this.renderSendButton}
          commentField={this.renderField()}
          isCommentEnabled={true}
          saveLoading={this.props.saveLoading}
          company={this.props.company}
          title={t(cardTitle)}
          t={this.props.t}
          showSpinner={false}
          initialValues={this.props.initialValues}
        />
      );
    }
    return <div />;
  }
}

const mapStateToProps = (state: StoreI, ownProps: OwnProps): StateProps => {
  const currentCard = {
    managerSelection: ownProps?.currentCard?.managerSelection || [],
    selfSelection: ownProps?.currentCard?.selfSelection || [],
    customSelection: ownProps?.currentCard?.customSelection || [],
  };

  const allSegments = uniqBy([
    ...currentCard.managerSelection,
    ...currentCard.selfSelection,
    ...currentCard.customSelection,
  ], 'id');

  const lockedSegments = getLockedSegments(ownProps.selectionType, currentCard);

  return {
    company: state.user.session.company,
    initialValues: {
      selectedPeople: (ownProps.currentCard && allSegments) || [],
    },
    noneRemovableIds: lockedSegments.map((item) => item.id),
  };
};

const SelectionCardSuperComponent = connect(mapStateToProps)(withTranslation()(SelectionCard));
export default SelectionCardSuperComponent;
